/**
 * Vanilla JS wrapper for announcements.
 * Renders a floating badge with dropdown without React dependency.
 */
import { UITLApiClient } from '../api';
import type { UITLConfig, Announcement } from '../types';

export class VanillaAnnouncementWidget {
  private api: UITLApiClient;
  private container: HTMLElement;
  private isOpen = false;
  private announcements: Announcement[] = [];
  private readIds = new Set<number>();
  private primaryColor: string;

  constructor(config: UITLConfig) {
    this.api = new UITLApiClient(config);
    this.primaryColor = config.theme?.primaryColor || '#2563EB';
    this.container = document.createElement('div');
    this.container.id = 'uitl-announcement-widget';
    document.body.appendChild(this.container);
    this.load();
  }

  private async load() {
    try {
      this.announcements = await this.api.getAnnouncements();
      this.render();
    } catch (e) {
      console.warn('[UITL] Failed to load announcements:', e);
    }
  }

  private toggle() {
    this.isOpen = !this.isOpen;
    this.render();
  }

  private async markRead(id: number) {
    this.readIds.add(id);
    this.render();
    try { await this.api.markAnnouncementRead(id); } catch (e) { /* best-effort */ }
  }

  private get unreadCount() {
    return this.announcements.filter(a => !this.readIds.has(a.id)).length;
  }

  private typeEmoji(type: string): string {
    const map: Record<string, string> = { feature: '🎉', fix: '🔧', maintenance: '⚠️', info: 'ℹ️' };
    return map[type] || 'ℹ️';
  }

  private typeColor(type: string): string {
    const map: Record<string, string> = { feature: '#10b981', fix: '#3b82f6', maintenance: '#f59e0b', info: '#6366f1' };
    return map[type] || '#6366f1';
  }

  private render() {
    const c = this.primaryColor;
    const count = this.unreadCount;

    if (!this.isOpen) {
      this.container.innerHTML = `
        <button id="uitl-ann-fab" style="position:fixed;bottom:150px;right:24px;width:44px;height:44px;border-radius:50%;background:white;border:2px solid ${c};cursor:pointer;font-size:18px;box-shadow:0 2px 8px rgba(0,0,0,0.15);z-index:99997;display:flex;align-items:center;justify-content:center;position:relative;">
          📢
          ${count > 0 ? `<span style="position:absolute;top:-4px;right:-4px;width:18px;height:18px;background:#ef4444;color:white;border-radius:50%;font-size:11px;display:flex;align-items:center;justify-content:center;font-weight:bold;">${count}</span>` : ''}
        </button>
      `;
      this.container.querySelector('#uitl-ann-fab')?.addEventListener('click', () => this.toggle());
      return;
    }

    const items = this.announcements.map(a => {
      const isRead = this.readIds.has(a.id);
      const typeColor = this.typeColor(a.announcement_type);
      return `
        <div class="uitl-ann-item" data-id="${a.id}" style="padding:12px;border-bottom:1px solid #f3f4f6;cursor:pointer;${isRead ? 'opacity:0.7;' : ''}">
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:4px;">
            <span style="background:${typeColor}20;color:${typeColor};padding:2px 8px;border-radius:12px;font-size:11px;font-weight:600;">${this.typeEmoji(a.announcement_type)} ${a.announcement_type}</span>
            ${!isRead ? '<span style="width:8px;height:8px;background:#ef4444;border-radius:50%;display:inline-block;"></span>' : ''}
          </div>
          <div style="font-weight:600;font-size:14px;margin-bottom:4px;">${this.escapeHtml(a.title)}</div>
          <div style="font-size:13px;color:#6b7280;line-height:1.4;">${this.escapeHtml(a.body).substring(0, 150)}${a.body.length > 150 ? '...' : ''}</div>
        </div>
      `;
    }).join('');

    this.container.innerHTML = `
      <div style="position:fixed;bottom:150px;right:24px;width:360px;max-height:400px;background:white;border-radius:12px;box-shadow:0 8px 30px rgba(0,0,0,0.2);z-index:99999;overflow:hidden;display:flex;flex-direction:column;">
        <div style="padding:12px 16px;border-bottom:1px solid #e5e7eb;display:flex;justify-content:space-between;align-items:center;">
          <span style="font-weight:600;font-size:15px;">Announcements</span>
          <button id="uitl-ann-close" style="background:none;border:none;font-size:18px;cursor:pointer;color:#9ca3af;">✕</button>
        </div>
        <div style="overflow-y:auto;flex:1;">
          ${items || '<div style="padding:24px;text-align:center;color:#9ca3af;font-size:14px;">No announcements</div>'}
        </div>
      </div>
    `;

    this.container.querySelector('#uitl-ann-close')?.addEventListener('click', () => this.toggle());
    this.container.querySelectorAll('.uitl-ann-item').forEach(el => {
      el.addEventListener('click', () => {
        const id = parseInt((el as HTMLElement).dataset.id || '0');
        if (id) this.markRead(id);
      });
    });
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  destroy() {
    this.container.remove();
  }
}
